import { useState, useEffect } from "react";
import { useRoute, Link } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Loader2, ChevronLeft, User, Edit, Trash2, FileText, Plus } from "lucide-react";
import { useToast } from '../hooks/use-toast';
import { apiRequest } from '../lib/queryClient';
import { authInterceptor } from '../lib/auth-interceptor';
import Sidebar from '../components/layout/sidebar';
import MobileSidebar from '../components/layout/mobile-sidebar';
import TopNav from '../components/layout/top-nav';
import ClientDetail from '../components/clients/client-detail';
import ClientForm from '../components/clients/client-form';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

interface Client {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
  createdAt: number;
  projects?: any[];
}

interface ClientInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  notes: string;
}

export default function ClientDetailPage() {
  const [, params] = useRoute<{ id: string }>("/clients/:id");
  const clientId = params?.id ? parseInt(params.id) : 0;
  const [isEditMode, setIsEditMode] = useState(false);
  const [isClientFormOpen, setIsClientFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch client data with better error handling and normalization
  const { data: client, isLoading, error } = useQuery<Client>({
    queryKey: ['/api/protected/clients', clientId],
    queryFn: async () => {
      if (!clientId || isNaN(clientId) || clientId <= 0) {
        throw new Error('Invalid client ID');
      }
      const response = await fetch(`/api/protected/clients?id=${clientId}`, {
        credentials: 'include',
        headers: {
          ...authInterceptor.addAuthHeaders()
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch client: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      if (!data || !data.id) {
        throw new Error('Invalid client data received');
      }
      
      // Normalize data - API returns camelCase already, but handle both formats
      const normalized: Client = {
        id: data.id,
        firstName: data.firstName || data.first_name || '',
        lastName: data.lastName || data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        notes: data.notes || '',
        // Handle createdAt - could be ISO string, timestamp, or number
        createdAt: data.createdAt 
          ? (typeof data.createdAt === 'string' 
              ? new Date(data.createdAt).getTime() 
              : typeof data.createdAt === 'number' 
                ? (data.createdAt < 10000000000 ? data.createdAt * 1000 : data.createdAt)
                : Date.now())
          : (data.created_at 
              ? (typeof data.created_at === 'string' 
                  ? new Date(data.created_at).getTime() 
                  : typeof data.created_at === 'number' 
                    ? (data.created_at < 10000000000 ? data.created_at * 1000 : data.created_at)
                    : Date.now())
              : Date.now()),
        projects: data.projects || []
      };
      
      return normalized;
    },
    enabled: clientId !== null && !isNaN(clientId) && clientId > 0,
    retry: 2,
    retryDelay: 1000,
  });

  // Update client mutation
  const updateClientMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ClientInput }) => {
      const response = await apiRequest("PATCH", `/api/protected/clients?id=${id}`, data);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients'] });
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients', clientId] });
      setIsClientFormOpen(false);
      setIsEditMode(false);
      toast({ 
        title: 'Success', 
        description: 'Client updated successfully' 
      });
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message, 
        variant: 'destructive' 
      });
    }
  });

  // Delete client mutation
  const deleteClientMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest("DELETE", `/api/protected/clients?id=${id}`);
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/protected/clients'] });
      toast({ 
        title: 'Success', 
        description: 'Client deleted successfully' 
      });
      // Redirect to clients list after deletion
      window.location.href = '/clients';
    },
    onError: (error: any) => {
      toast({ 
        title: 'Error', 
        description: error.message, 
        variant: 'destructive' 
      });
    }
  });

  const handleEditClient = () => {
    setIsEditMode(true);
    setIsClientFormOpen(true);
  };

  const handleDeleteClient = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (client) {
      deleteClientMutation.mutate(client.id);
    }
    setIsDeleteDialogOpen(false);
  };

  const handleClientFormSubmit = (data: ClientInput) => {
    if (client) {
      updateClientMutation.mutate({ id: client.id, data });
    }
  };

  const handleNewEstimate = () => {
    if (client) {
      window.location.href = `/estimates/create?clientId=${client.id}`;
    } else {
      window.location.href = '/estimates/create';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="remodra-layout">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content">
            <div className="container py-8">
              <div className="remodra-card p-8 border-green-500/30">
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                  <Loader2 className="h-12 w-12 animate-spin text-green-500 mb-4" />
                  <span className="text-lg text-slate-600 dark:text-slate-300 font-medium">Loading client...</span>
                  <span className="text-sm text-slate-400 dark:text-slate-500 mt-2">Please wait</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || (!isLoading && !client)) {
    return (
      <div className="remodra-layout">
        <div className="hidden md:block">
          <Sidebar />
        </div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content">
            <div className="container py-8">
              <div className="remodra-card p-8 border-red-500/30">
                <div className="mb-6">
                  <Link href="/clients">
                    <Button variant="ghost" className="mb-4 remodra-button-outline">
                      <ChevronLeft className="h-4 w-4 mr-2" />
                      Back to Clients
                    </Button>
                  </Link>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-red-500" />
                    </div>
                    <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200">Error Loading Client</h1>
                  </div>
                  
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                    <p className="text-red-800 dark:text-red-200 font-medium mb-2">
                      {error 
                        ? `Error: ${error instanceof Error ? error.message : 'Unknown error'}` 
                        : "Could not find the requested client. It may have been deleted or you don't have access to it."
                      }
                    </p>
                    {clientId && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        Client ID: {clientId}
                      </p>
                    )}
                  </div>
                  
                  <div className="mt-6">
                    <Button 
                      onClick={() => window.location.href = '/clients'}
                      className="remodra-button"
                    >
                      Go to Clients List
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main content
  return (
    <div className="remodra-layout">
      <div className="hidden md:block">
        <Sidebar />
      </div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <div className="container py-8">
            {/* Header */}
            <div className="mb-8">
              <Link href="/clients">
                <Button variant="ghost" className="mb-4 remodra-button-outline">
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Back to Clients
                </Button>
              </Link>
              
              <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-4">
                {client?.firstName} {client?.lastName}
              </h1>
            </div>

            {/* Client Detail Component */}
            {client && (
              <div className="remodra-card p-6 border-green-500/30">
                <ClientDetail 
                  client={client}
                  onEdit={handleEditClient}
                  onDelete={handleDeleteClient}
                  onNewEstimate={handleNewEstimate}
                />
              </div>
            )}

            {/* Client Form Dialog */}
            <Dialog open={isClientFormOpen} onOpenChange={setIsClientFormOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {isEditMode ? "Edit Client" : "Add New Client"}
                  </DialogTitle>
                  <DialogDescription>
                    {isEditMode 
                      ? "Update the client information below"
                      : "Enter the details for the new client"}
                  </DialogDescription>
                </DialogHeader>
                {client && (
                  <ClientForm 
                    client={isEditMode ? {
                      id: client.id,
                      firstName: client.firstName,
                      lastName: client.lastName,
                      email: client.email,
                      phone: client.phone,
                      address: client.address,
                      city: client.city,
                      state: client.state,
                      zip: client.zip,
                      notes: client.notes,
                      createdAt: client.createdAt
                    } : undefined}
                    onSubmit={handleClientFormSubmit}
                    isSubmitting={updateClientMutation.isPending}
                    onCancel={() => {
                      setIsClientFormOpen(false);
                      setIsEditMode(false);
                    }}
                  />
                )}
              </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete {client?.firstName} {client?.lastName}? 
                    This action cannot be undone and will remove all data associated with this client.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleConfirmDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
}

